import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FolderOpen,
  X,
} from 'lucide-react';
import type { ImportCommitResponse, ImportMode, ReceptieParseResponse } from '@forest/contracts';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useImportCommit, useReceptieImportParse } from './useImport';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { ImportProblems } from './ImportProblems';

type Step = 'upload' | 'preview' | 'commit' | 'done';
const STEPS: Step[] = ['upload', 'preview', 'commit'];

type Picked = { file: File; relativePath: string };

/** `webkitdirectory` is non-standard but universally supported, and it
 *  is the only way the browser hands us the folder each file came from —
 *  which is where the campaign year and season live. */
type DirectoryInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  webkitdirectory?: string;
  directory?: string;
};

function relativePathOf(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return rel && rel.length > 0 ? rel : file.name;
}

/**
 * Import flow for technical-reception archives.
 *
 * Unlike the other targets this takes a whole FOLDER: one zip per
 * survey, and the campaign (year + season) is only knowable from the
 * directory name. A flat multi-file selection still works, but then the
 * campaign has to be chosen by hand.
 */
export function ReceptiiImportPanel() {
  const { t } = useTranslation(['import', 'common']);
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const parseMut = useReceptieImportParse();
  const commitMut = useImportCommit();

  const [picked, setPicked] = useState<Picked[]>([]);
  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ReceptieParseResponse | null>(null);
  const [committed, setCommitted] = useState<ImportCommitResponse | null>(null);
  const [mode, setMode] = useState<ImportMode>('skip_invalid');
  const [fallbackYear, setFallbackYear] = useState('');
  const [fallbackSezon, setFallbackSezon] = useState<'PRIMAVARA' | 'TOAMNA'>('PRIMAVARA');
  const dirRef = useRef<HTMLInputElement>(null);

  const stepIndex = STEPS.indexOf(step);

  const addFiles = useCallback((list: FileList | null) => {
    if (!list) return;
    const zips = Array.from(list)
      .filter((f) => f.name.toLowerCase().endsWith('.zip'))
      .map((f) => ({ file: f, relativePath: relativePathOf(f) }));
    if (zips.length === 0) return;
    setPicked((prev) => {
      const byPath = new Map(prev.map((p) => [p.relativePath, p]));
      for (const z of zips) byPath.set(z.relativePath, z);
      return Array.from(byPath.values()).sort((a, b) =>
        a.relativePath.localeCompare(b.relativePath),
      );
    });
  }, []);

  /** True when at least one archive lacks a recognisable campaign
   *  folder — then the manual year/season is the only way to place it. */
  const needsFallback = useMemo(
    () =>
      picked.length > 0 &&
      picked.some((p) => !/\b(pr|tm)\.?\s*\d{4}\b/i.test(p.relativePath)),
    [picked],
  );

  const byFolder = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of picked) {
      const folder = p.relativePath.split('/').slice(0, -1).join('/') || '—';
      m.set(folder, (m.get(folder) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [picked]);

  const onParse = async () => {
    if (picked.length === 0) return;
    if (needsFallback && !/^\d{4}$/.test(fallbackYear)) {
      pushToast({
        variant: 'error',
        message: t('receptii.needCampaign', {
          defaultValue:
            'Unele arhive nu au folder de campanie — alegeți anul și sezonul.',
        }),
      });
      return;
    }
    try {
      const result = await parseMut.mutateAsync({
        files: picked,
        fallback: needsFallback
          ? { anul: Number(fallbackYear), sezon: fallbackSezon }
          : undefined,
      });
      setParsed(result);
      setStep('preview');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const onCommit = async () => {
    if (!parsed) return;
    try {
      const result = await commitMut.mutateAsync({ batchId: parsed.batchId, mode });
      setCommitted(result);
      setStep('done');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  return (
    <div className="space-y-6">
      {step !== 'done' && (
        <div className="flex items-center justify-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all',
                  i < stepIndex
                    ? 'bg-forest-500 text-white'
                    : i === stepIndex
                      ? 'bg-forest-600 text-white ring-4 ring-forest-100 dark:ring-forest-900/40'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
                )}
              >
                {i < stepIndex ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={cn(
                    'mx-2 h-px w-8',
                    i < stepIndex ? 'bg-forest-400' : 'bg-slate-200 dark:bg-slate-700',
                  )}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {step === 'upload' && (
        <div className="space-y-4">
          <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center dark:border-slate-600 dark:bg-slate-800/60">
            <FolderOpen className="mx-auto mb-3 h-10 w-10 text-slate-400 dark:text-slate-500" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {t('receptii.pickFolder', {
                defaultValue: 'Alegeți folderul cu arhivele de recepție',
              })}
            </p>
            <p className="mx-auto mt-1 max-w-lg text-xs text-slate-400 dark:text-slate-500">
              {t('receptii.pickHint', {
                defaultValue:
                  'Câte un .zip per recepție. Anul și sezonul se citesc din numele folderului (ex. „… pr.2024" = primăvara 2024), restul datelor din numele arhivei.',
              })}
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <Button type="button" onClick={() => dirRef.current?.click()}>
                {t('receptii.chooseFolder', { defaultValue: 'Alege folder' })}
              </Button>
            </div>
            <input
              ref={dirRef}
              type="file"
              multiple
              onChange={(e) => addFiles(e.target.files)}
              className="hidden"
              {...({ webkitdirectory: '', directory: '' } as DirectoryInputProps)}
            />
          </div>

          {picked.length > 0 && (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {t('receptii.selected', {
                      defaultValue: '{{count}} arhive selectate',
                      count: picked.length,
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPicked([])}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <X className="h-3.5 w-3.5" />
                    {t('common:action.clear', { defaultValue: 'Golește' })}
                  </button>
                </div>
                <ul className="max-h-48 space-y-1 overflow-auto text-xs">
                  {byFolder.map(([folder, n]) => (
                    <li
                      key={folder}
                      className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 dark:bg-slate-800"
                    >
                      <span className="truncate text-slate-700 dark:text-slate-200">{folder}</span>
                      <span className="ml-3 shrink-0 tabular-nums text-slate-500">{n}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}

          {needsFallback && (
            <div className="flex flex-col gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 sm:flex-row sm:items-end">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  {t('receptii.fallbackHint', {
                    defaultValue:
                      'Unele arhive nu au un folder de campanie. Alegeți anul și sezonul pentru ele.',
                  })}
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  value={fallbackYear}
                  onChange={(e) => setFallbackYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="2024"
                  inputMode="numeric"
                  className="w-24 rounded-md border border-amber-300 bg-white px-2 py-1 text-sm text-slate-800"
                  aria-label={t('receptii.year', { defaultValue: 'An' })}
                />
                <Select
                  value={fallbackSezon}
                  onChange={(e) => setFallbackSezon(e.target.value as 'PRIMAVARA' | 'TOAMNA')}
                  uiSize="sm"
                >
                  <option value="PRIMAVARA">
                    {t('receptii.sezon.PRIMAVARA', { defaultValue: 'Primăvara' })}
                  </option>
                  <option value="TOAMNA">
                    {t('receptii.sezon.TOAMNA', { defaultValue: 'Toamna' })}
                  </option>
                </Select>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={onParse} loading={parseMut.isPending} disabled={picked.length === 0}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {step === 'preview' && parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label={t('preview.rows', { defaultValue: 'Arhive' })}
              value={parsed.validCount + parsed.invalidCount}
              color="slate"
            />
            <StatCard
              label={t('preview.valid', { defaultValue: 'Valide' })}
              value={parsed.validCount}
              color="green"
            />
            <StatCard
              label={t('receptii.duplicates', { defaultValue: 'Deja importate' })}
              value={parsed.duplicateCount}
              color={parsed.duplicateCount > 0 ? 'amber' : 'slate'}
            />
            <StatCard
              label={t('preview.invalid', { defaultValue: 'Sărite' })}
              value={parsed.invalidCount}
              color={parsed.invalidCount > 0 ? 'red' : 'slate'}
            />
          </div>

          <div className="space-y-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
            <div>
              {t('receptii.campaigns', {
                defaultValue: 'Campanii: {{list}}',
                list: parsed.campaigns
                  .map(
                    (c) =>
                      `${t(`receptii.sezon.${c.sezon}`, {
                        defaultValue: c.sezon === 'PRIMAVARA' ? 'Primăvara' : 'Toamna',
                      })} ${c.anul}`,
                  )
                  .join(', '),
              })}
            </div>
            <div>
              {t('receptii.normPrimarie', {
                defaultValue:
                  'Primării: {{matched}} găsite în registru, {{unmatched}} păstrate ca în fișier',
                matched: parsed.normalized.primarieMatched,
                unmatched: parsed.normalized.primarieUnmatched,
              })}
            </div>
          </div>

          <ImportProblems
            problems={parsed.errors.map((e, i) => ({
              rawIndex: i,
              code: e.code ?? 'other',
              reason: e.reason,
              context: { arhiva: e.sourceFile, motiv: e.reason },
            }))}
            rowLabel={(p) => (p.context?.arhiva ?? '').split('/').pop() ?? ''}
            fileName="probleme-receptii.csv"
          />

          <Card>
            <CardBody className="max-h-72 overflow-auto p-0">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">Primăria</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Raion</th>
                    <th className="px-2 py-1.5 text-left font-semibold">UCF</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Parcele</th>
                    <th className="px-2 py-1.5 text-right font-semibold">ha decl.</th>
                    <th className="px-2 py-1.5 text-right font-semibold">ha calc.</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Campanie</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.preview.map((r) => (
                    <tr
                      key={r.sourceFile}
                      className={cn(
                        'border-t border-slate-100 dark:border-slate-800',
                        r.duplicate && 'bg-amber-50 dark:bg-amber-900/10',
                      )}
                    >
                      <td className="px-2 py-1.5">{r.primarie}</td>
                      <td className="px-2 py-1.5">{r.raion ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{r.ucf}</td>
                      <td className="px-2 py-1.5 font-mono">{r.parcele}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {r.suprafataDeclarata?.toFixed(2) ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {r.suprafataCalculata?.toFixed(2) ?? '—'}
                      </td>
                      <td className="px-2 py-1.5">
                        {t(`receptii.sezon.${r.sezon}`, {
                          defaultValue: r.sezon === 'PRIMAVARA' ? 'Primăvara' : 'Toamna',
                        })}{' '}
                        {r.anul}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep('upload')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common:action.back')}
            </Button>
            <Button onClick={() => setStep('commit')} disabled={parsed.validCount === 0}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {step === 'commit' && parsed && (
        <Card>
          <CardBody className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t('commit.mode', { defaultValue: 'Modul de import' })}
              </label>
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value as ImportMode)}
                className="mt-1"
              >
                <option value="skip_invalid">
                  {t('commit.modeSkip', { defaultValue: 'Sari peste rânduri invalide' })}
                </option>
                <option value="all_or_nothing">
                  {t('commit.modeAll', { defaultValue: 'Tot sau nimic' })}
                </option>
              </Select>
            </div>
            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep('preview')}>
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                {t('common:action.back')}
              </Button>
              <Button onClick={onCommit} loading={commitMut.isPending}>
                {t('common:action.confirm')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {step === 'done' && committed && (
        <Card>
          <CardBody className="space-y-3 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest-100 text-forest-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('done.title', { defaultValue: 'Import finalizat' })}
            </h2>
            <p className="text-sm text-slate-600">
              {t('sub.doneSummary', {
                defaultValue:
                  'Noi: {{imported}} · Actualizate: {{updated}} · Neschimbate: {{unchanged}} · Sărite: {{skipped}}',
                imported: committed.imported,
                updated: committed.updated ?? 0,
                unchanged: committed.unchanged ?? 0,
                skipped: committed.skipped,
              })}
            </p>
            {committed.errors.length > 0 && (
              <div className="pt-2 text-left">
                <ImportProblems
                  problems={committed.errors.map((e) => ({
                    rawIndex: e.row,
                    code: 'commit',
                    reason: e.message,
                  }))}
                  fileName="probleme-salvare.csv"
                />
              </div>
            )}
            {(committed as { unattached?: number }).unattached ? (
              <p className="text-sm text-amber-700">
                {t('receptii.unattached', {
                  defaultValue:
                    '{{count}} nu se află în niciun poligon și au rămas fără poligon (butonul „Fără poligon" de pe Poligoane).',
                  count: (committed as { unattached?: number }).unattached,
                })}
              </p>
            ) : null}
            <div className="flex justify-center gap-3 pt-2">
              <Button variant="secondary" onClick={() => navigate('/app/parcels?sub=with')}>
                {t('receptii.viewAll', { defaultValue: 'Vezi poligoanele cu subpoligoane' })}
              </Button>
              <Button
                onClick={() => {
                  setPicked([]);
                  setParsed(null);
                  setCommitted(null);
                  setStep('upload');
                }}
              >
                {t('done.again', { defaultValue: 'Import nou' })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {(parseMut.isPending || commitMut.isPending) && (
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
          <Spinner /> {t('common:state.loading', { defaultValue: 'Se procesează…' })}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: 'slate' | 'green' | 'red' | 'amber';
}) {
  const colorClass = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  }[color];
  return (
    <div className={cn('rounded-lg border px-3 py-2', colorClass)}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
